import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Trophy, Users, Target, Flame, Bell, X,
  MessageCircle, Share2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { InAppNotification } from '@/hooks/useInAppNotifications';

interface NotificationItemProps {
  notification: InAppNotification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onClick?: () => void;
}

const typeIcons = {
  achievement: Trophy,
  challenge: Target,
  group_message: MessageCircle,
  goal_reached: Target,
  streak_alert: Flame,
  system: Bell,
  shared_simulation: Share2,
};

const typeColors = {
  achievement: 'text-amber-500',
  challenge: 'text-blue-500',
  group_message: 'text-green-500',
  goal_reached: 'text-emerald-500',
  streak_alert: 'text-orange-500',
  system: 'text-muted-foreground',
  shared_simulation: 'text-indigo-500',
};

export function NotificationItem({ 
  notification, 
  onMarkAsRead, 
  onDelete,
  onClick 
}: NotificationItemProps) {
  const Icon = typeIcons[notification.type] || Bell;
  const iconColor = typeColors[notification.type] || 'text-muted-foreground';

  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
    onClick?.();
  };

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer group",
        notification.read 
          ? "bg-transparent hover:bg-muted/50" 
          : "bg-primary/5 hover:bg-primary/10"
      )}
      onClick={handleClick}
    >
      <div className={cn(
        "flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center",
        notification.read ? "bg-muted" : "bg-primary/10"
      )}>
        <Icon className={cn("h-4 w-4", iconColor)} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={cn(
            "text-sm line-clamp-1",
            notification.read ? "text-muted-foreground" : "font-medium text-foreground"
          )}>
            {notification.title}
          </p>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 opacity-0 group-hover:opacity-100 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(notification.id);
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
          {notification.message}
        </p>
        <p className="text-[10px] text-muted-foreground mt-1">
          {format(new Date(notification.created_at), "d MMM 'às' HH:mm", { locale: ptBR })}
        </p>
      </div>

      {!notification.read && (
        <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
      )}
    </div>
  );
}
